import { Component, OnDestroy, OnInit, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";

import { DirectionService } from "../../direction.service";
import { TranslateService } from "@ngx-translate/core";
import {
  PrintNodeJobOutcome,
  PrintNodeStatusDialogFileRow,
  PrintNodeStatusDialogViewModel,
} from "../../other-pages/my-orders/orders-service";

export interface PrintNodeStatusDialogData {
  orderId: string;
  viewModel: PrintNodeStatusDialogViewModel;
}

@Component({
  selector: "app-printnode-status-dialog",
  templateUrl: "./printnode-status-dialog.component.html",
  styleUrls: ["./printnode-status-dialog.component.css"],
  host: {
    class: "fill-screen-modal-phone",
  },
})
export class PrintNodeStatusDialogComponent implements OnInit, OnDestroy {
  isRTL = true;
  isDarkMode = false;
  private directionSub?: Subscription;
  private darkSub?: Subscription;

  orderId: string;
  viewModel: PrintNodeStatusDialogViewModel;

  constructor(
    private directionService: DirectionService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<PrintNodeStatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PrintNodeStatusDialogData
  ) {
    this.orderId = data?.orderId ?? "";
    this.viewModel = data?.viewModel ?? { isEmpty: true, printer: null, files: [] };
  }

  ngOnInit(): void {
    this.directionSub = this.directionService.direction$.subscribe((dir) => {
      this.isRTL = dir === "rtl";
    });
    this.darkSub = this.directionService.isDarkMode$.subscribe((d) => {
      this.isDarkMode = d;
    });
  }

  ngOnDestroy(): void {
    this.directionSub?.unsubscribe();
    this.darkSub?.unsubscribe();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  jobStateLabel(raw: string): string {
    if (!raw || raw === "—") return "—";
    const key = "su-management.printnode-job-state." + raw;
    const t = this.translate.instant(key);
    return t !== key ? t : raw;
  }

  printerStateLabel(raw: string): string {
    if (!raw) return "—";
    const key = "su-management.printnode-printer-state." + raw;
    const t = this.translate.instant(key);
    return t !== key ? t : raw;
  }

  computerStateLabel(raw: string): string {
    if (!raw) return "—";
    const key = "su-management.printnode-computer-state." + raw;
    const t = this.translate.instant(key);
    return t !== key ? t : raw;
  }

  fileRowClass(f: PrintNodeStatusDialogFileRow): string {
    switch (f.outcome) {
      case "done":
        return "printnode-file-row printnode-file-row--done";
      case "error":
        return "printnode-file-row printnode-file-row--error";
      case "progress":
        return "printnode-file-row printnode-file-row--progress";
      default:
        return "printnode-file-row printnode-file-row--unknown";
    }
  }

  fileIcon(f: PrintNodeStatusDialogFileRow): string {
    switch (f.outcome) {
      case "done":
        return "check_circle";
      case "error":
        return "error";
      case "progress":
        return "pending";
      default:
        return "help_outline";
    }
  }

  fileIconClass(f: PrintNodeStatusDialogFileRow): string {
    switch (f.outcome) {
      case "done":
        return "printnode-ico printnode-ico--ok";
      case "error":
        return "printnode-ico printnode-ico--err";
      case "progress":
        return "printnode-ico printnode-ico--prog";
      default:
        return "printnode-ico printnode-ico--unk";
    }
  }

  outcomeHint(outcome: PrintNodeJobOutcome): string {
    const key = "su-management.printnode-dialog-outcome-hint." + outcome;
    const t = this.translate.instant(key);
    return t !== key ? t : "";
  }
}
